/**
 * Expanded detail for a new-booking mail feed event
 * (`payload.kind === "new_booking_mail"`). Booking-centric: shows the parsed
 * booking snapshot (route, parties, containers) and any matching rates found in
 * the system, with no email body or classification chrome.
 */
import { PackagePlus } from "lucide-react";

import {
  SensitiveContractRef,
  SensitiveMoney,
  SensitiveText,
} from "@/components/common/Sensitive";
import { formatDateTimePlain } from "@/lib/weekDates";
import type {
  FeedEffectPayload,
  FeedEntityScope,
  NewBookingMailPayload,
} from "@/types/feed";
import { bookingOpportunityDisplayTitle } from "./bookingOpportunityDisplay";
import { commercialDigestCopyFromEvent } from "./commercialDigestCopy";
import { personalizeFeedEventText } from "./feedViewerParty";
import {
  rateIngestDisplaySummary,
  rateIngestDisplayTitle,
  type RateIngestDisplayMeta,
} from "./rateIngestDisplay";

/** Type guard: does this payload carry a new-booking mail snapshot? */
export function isNewBookingMailPayload(
  payload: FeedEffectPayload,
): payload is NewBookingMailPayload {
  return (payload as { kind?: string })?.kind === "new_booking_mail";
}

function MetaRow({
  label,
  value,
  mask = false,
}: {
  label: string;
  value: string;
  mask?: boolean;
}) {
  return (
    <div className="flex gap-2 text-[11px]">
      <span className="w-20 shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 flex-1 break-words text-muted-foreground">
        {mask ? <SensitiveText value={value} /> : value}
      </span>
    </div>
  );
}

function containerLabel(container: {
  equipment_size: string | null;
  equipment_type: string | null;
  quantity: number;
}): string {
  const size = container.equipment_size?.trim();
  const eqType = container.equipment_type?.trim();
  if (size && eqType) return `${container.quantity}x ${size}' ${eqType}`;
  if (size) return `${container.quantity}x ${size}'`;
  if (eqType) return `${container.quantity}x ${eqType}`;
  return `${container.quantity} ctr`;
}

/** Feed title: consignee · lane · equipment — no event-name prefix or booking id. */
export function newBookingMailTitle(
  payload: NewBookingMailPayload,
): string | null {
  const origin = payload.origin_port?.trim() || "";
  const dest = payload.destination_port?.trim() || "";
  if (!origin && !dest) return null;
  const route = origin && dest ? `${origin} → ${dest}` : origin || dest;
  const consignee = payload.consignee?.trim() || null;
  const equipment =
    payload.containers?.length > 0
      ? payload.containers.map(containerLabel).join(", ")
      : null;
  if (consignee && equipment) return `${consignee} · ${route} · ${equipment}`;
  if (consignee) return `${consignee} · ${route}`;
  if (equipment) return `${route} · ${equipment}`;
  return route;
}

/** Prefer derived titles for digest, new-booking mail, and rate-ingest cards. */
export function feedEventDisplayTitle(event: {
  title: string;
  payload?: unknown;
  entity_scope?: FeedEntityScope | null;
}): string {
  const digest = commercialDigestCopyFromEvent(event);
  if (digest) return personalizeFeedEventText(digest.title, event);
  const bookingOppTitle = bookingOpportunityDisplayTitle(event);
  if (bookingOppTitle) {
    return personalizeFeedEventText(bookingOppTitle, event);
  }
  if (isNewBookingMailPayload(event.payload as FeedEffectPayload)) {
    const title =
      newBookingMailTitle(event.payload as NewBookingMailPayload) ?? event.title;
    return personalizeFeedEventText(title, event);
  }
  const title = rateIngestDisplayTitle(event) ?? event.title;
  return personalizeFeedEventText(title, event);
}

/** Prefer derived summaries for digest and rate-ingest cards. */
export function feedEventDisplaySummary(
  event: {
    summary?: string | null;
    payload?: unknown;
    entity_scope?: FeedEntityScope | null;
  },
  meta?: RateIngestDisplayMeta | null,
): string | null {
  const digest = commercialDigestCopyFromEvent(event);
  if (digest) return digest.summary;
  const summary = rateIngestDisplaySummary(event, meta) ?? event.summary ?? null;
  return summary ? personalizeFeedEventText(summary, event) : null;
}

export function NewBookingMailFeedDetail({
  payload,
}: {
  payload: NewBookingMailPayload;
}) {
  const route = `${payload.origin_port} → ${payload.destination_port}`;

  return (
    <div className="flex flex-col gap-2.5 rounded-lg bg-white/5 p-3">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          <PackagePlus className="size-3" />
          New booking
        </span>
        {payload.received_at && (
          <span className="text-[11px] tabular-nums text-muted-foreground">
            {formatDateTimePlain(payload.received_at)}
          </span>
        )}
      </div>

      <p className="text-sm font-medium leading-snug break-words">{route}</p>

      <div className="flex flex-col gap-1 border-t border-border/40 pt-2">
        <MetaRow label="Booking" value={payload.booking_source_id} />
        {payload.consignee && (
          <MetaRow label="Consignee" value={payload.consignee} mask />
        )}
        {payload.shipper_name && (
          <MetaRow label="Shipper" value={payload.shipper_name} mask />
        )}
        {payload.commodity && (
          <MetaRow label="Commodity" value={payload.commodity} />
        )}
        {payload.departure_date && (
          <MetaRow label="ETD" value={payload.departure_date} />
        )}
        {payload.containers.length > 0 && (
          <MetaRow
            label="Containers"
            value={payload.containers.map(containerLabel).join(", ")}
          />
        )}
        <MetaRow label="Mailbox" value={payload.mailbox_email} mask />
      </div>

      {payload.rates.length > 0 && (
        <div className="mt-0.5 flex flex-col gap-1 border-t border-border/40 pt-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Matching rates
          </span>
          {payload.rates.map((rate, i) => (
            <div
              key={`${rate.carrier}:${rate.contract_ref}:${i}`}
              className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground"
            >
              <span className="min-w-0 truncate">
                {rate.carrier.toUpperCase()}
                {rate.contract_ref ? (
                  <>
                    {" · "}
                    <SensitiveContractRef
                      value={rate.contract_ref}
                      carrier={rate.carrier}
                    />
                  </>
                ) : null}
                <span className="ml-1 rounded-full bg-white/5 px-1.5 py-0.5 text-[9px] uppercase">
                  {rate.match_class}
                </span>
              </span>
              <span className="shrink-0 tabular-nums">
                <SensitiveMoney amount={rate.frt_usd} />
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
